import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary action buttons - for main actions (create, save, etc.) - Premium gradient effect
        primary:
          'transform border-0 bg-gradient-to-br from-primary-600 to-primary-700 text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:from-primary-700 hover:to-primary-800 hover:shadow-lg active:translate-y-0 active:shadow-md dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700',

        // Secondary action buttons - supporting actions, less emphasis
        secondary:
          'transform bg-gradient-to-br from-secondary-500 to-secondary-600 text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:from-secondary-600 hover:to-secondary-700 hover:shadow-lg active:translate-y-0 dark:from-secondary-400 dark:to-secondary-500 dark:hover:from-secondary-500 dark:hover:to-secondary-600',

        // Tertiary - sophisticated deep blue for premium actions
        tertiary:
          'transform bg-gradient-to-br from-tertiary-800 to-tertiary-900 text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:from-tertiary-700 hover:to-tertiary-800 hover:shadow-lg active:translate-y-0 dark:from-tertiary-600 dark:to-tertiary-700 dark:hover:from-tertiary-500 dark:hover:to-tertiary-600',

        // Success - for confirming actions, positive outcomes
        success:
          'transform bg-gradient-to-br from-success-600 to-success-700 text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:from-success-700 hover:to-success-800 hover:shadow-lg active:translate-y-0 dark:from-success-500 dark:to-success-600 dark:hover:from-success-600 dark:hover:to-success-700',

        // Destructive - for delete, remove actions
        destructive:
          'transform bg-gradient-to-br from-danger-600 to-danger-700 text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:from-danger-700 hover:to-danger-800 hover:shadow-lg active:translate-y-0 dark:from-danger-500 dark:to-danger-600 dark:hover:from-danger-600 dark:hover:to-danger-700',

        // Outline - bordered buttons for secondary options
        outline:
          'border-2 border-primary-200 bg-white text-primary-700 shadow-sm transition-all duration-200 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800 hover:shadow-md active:bg-primary-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-100 dark:active:bg-gray-600',

        // Secondary outline - alternative bordered style
        'outline-secondary':
          'border-2 border-secondary-200 bg-white text-secondary-700 shadow-sm transition-all duration-200 hover:border-secondary-300 hover:bg-secondary-50 hover:text-secondary-800 hover:shadow-md active:bg-secondary-100 dark:border-gray-600 dark:bg-gray-800 dark:text-secondary-400 dark:hover:border-secondary-500 dark:hover:bg-gray-700 dark:hover:text-secondary-300 dark:active:bg-gray-600',

        // Ghost - minimally visible buttons with hover effects
        ghost:
          'rounded-md text-enterprise-600 transition-all duration-200 hover:bg-enterprise-50 hover:text-enterprise-800 active:bg-enterprise-100 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100 dark:active:bg-gray-700',

        // Link style - for very minimal actions or within text
        link: 'text-primary-600 underline-offset-4 transition-colors duration-200 hover:text-primary-700 hover:underline dark:text-primary-400 dark:hover:text-primary-300',
      },
      size: {
        xs: 'h-7 rounded px-2 text-xs',
        sm: 'h-8 rounded px-3',
        default: 'h-10 rounded-md px-4 py-2',
        lg: 'h-11 rounded-md px-6',
        xl: 'h-12 rounded-md px-8 text-base',
        icon: 'size-10 rounded-md',
        'icon-sm': 'size-8 rounded-md',
      },
      fullWidth: {
        true: 'w-full',
      },
      withIcon: {
        true: 'items-center justify-center',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      fullWidth: false,
      withIcon: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      withIcon,
      asChild = false,
      isLoading = false,
      loadingText,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    const resolvedType = !asChild && !props.type ? 'button' : props.type;

    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            fullWidth,
            withIcon,
            className,
          })
        )}
        ref={ref}
        disabled={disabled || isLoading}
        type={resolvedType as any}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="-ml-1 mr-2 size-4 animate-spin text-current"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291a7.962 7.962 0 014-12.129V0c-4.418 0-8 3.582-8 8s3.582 8 8 8v-4a4 4 0 004-4h-4z"
              />
            </svg>
            {loadingText || children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
